import React from "react";
import { useShow } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Tag, Spin } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { 
    CalendarOutlined,
    EditOutlined,
    CheckCircleOutlined,
    LockOutlined,
    ClockCircleOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../../components/ui";

interface AccountingPeriod {
    id: number;
    periodId: string;
    year: number;
    month: number;
    startDate: string;
    endDate: string;
    status: number;
    closedDate?: string;
    closedBy?: string;
    createdAt: string;
    createdBy?: string;
    notes?: string;
}

const getStatusColor = (status: number) => {
    switch (status) {
        case 0: return "warning"; // Open
        case 1: return "success";  // Closed
        case 2: return "error";    // Locked
        default: return "default";
    }
};

const getStatusText = (status: number) => {
    switch (status) {
        case 0: return "Open";
        case 1: return "Closed";
        case 2: return "Locked";
        default: return "Unknown";
    }
};

const getStatusIcon = (status: number) => {
    switch (status) {
        case 0: return <ClockCircleOutlined />;
        case 1: return <CheckCircleOutlined />;
        case 2: return <LockOutlined />;
        default: return null;
    }
};

const months = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

export const AccountingPeriodsShow = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { queryResult } = useShow<AccountingPeriod>({
        resource: "accounting-periods",
        id,
    });
    const { data, isLoading } = queryResult;
    const record = data?.data;

    if (isLoading) {
        return (
            <div className="page-container flex items-center justify-center" style={{ minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    const periodTitle = record ? `${months[record.month]} ${record.year}` : "Accounting Period";

    return (
        <div className="page-container">
            <PageHeader
                title={periodTitle}
                titleIcon={<CalendarOutlined />}
                breadcrumbs={[
                    { title: "Accounting", href: "/accounting" },
                    { title: "Accounting Periods", href: "/accounting-periods" },
                    { title: record?.periodId || "Details" },
                ]}
                actions={[
                    {
                        label: "Edit",
                        onClick: () => navigate(`/accounting-periods/edit/${id}`),
                        type: "primary",
                        icon: <EditOutlined />,
                    },
                ]}
            >
                {/* Mini Stat Cards */}
                <div className="mini-stat-cards-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Period ID</div>
                        <div className="mini-stat-value">{record?.periodId || "-"}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Year</div>
                        <div className="mini-stat-value">{record?.year || "-"}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Month</div>
                        <div className="mini-stat-value">{record?.month ? months[record.month] : "-"}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Status</div>
                        <Tag 
                            color={getStatusColor(record?.status ?? -1)} 
                            icon={getStatusIcon(record?.status ?? -1)}
                        >
                            {getStatusText(record?.status ?? -1)}
                        </Tag>
                    </div>
                </div>
            </PageHeader>

            <div className="space-y-4">
                {/* Period Information Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Period Information</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Period ID:</span>
                                <span className="info-value font-semibold">{record?.periodId || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Status:</span>
                                <Tag 
                                    color={getStatusColor(record?.status ?? -1)} 
                                    icon={getStatusIcon(record?.status ?? -1)}
                                >
                                    {getStatusText(record?.status ?? -1)}
                                </Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Year:</span>
                                <span className="info-value">{record?.year || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Month:</span>
                                <span className="info-value">{record?.month ? months[record.month] : "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Start Date:</span>
                                <span className="info-value">
                                    {record?.startDate ? <DateField value={record.startDate} format="YYYY-MM-DD" /> : "-"}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">End Date:</span>
                                <span className="info-value">
                                    {record?.endDate ? <DateField value={record.endDate} format="YYYY-MM-DD" /> : "-"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Closure Information Card (only show if closed) */}
                {(record?.closedDate || record?.closedBy) && (
                    <div className="info-card">
                        <div className="info-card-header">
                            <h3 className="info-card-title">Closure Information</h3>
                        </div>
                        <div className="info-card-body">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Closed Date:</span>
                                    <span className="info-value">
                                        {record?.closedDate ? <DateField value={record.closedDate} format="YYYY-MM-DD HH:mm:ss" /> : "-"}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Closed By:</span>
                                    <span className="info-value">{record?.closedBy || "-"}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Audit Information Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Audit Information</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Created Date:</span>
                                <span className="info-value">
                                    {record?.createdAt ? <DateField value={record.createdAt} format="YYYY-MM-DD HH:mm:ss" /> : "-"}
                                </span>
                            </div>
                            {record?.createdBy && (
                                <div className="info-item">
                                    <span className="info-label">Created By:</span>
                                    <span className="info-value">{record.createdBy}</span>
                                </div>
                            )}
                            {record?.notes && (
                                <div className="info-item full-width">
                                    <span className="info-label">Notes:</span>
                                    <span className="info-value">{record.notes}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
